import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { RefreshCw, Calendar, Clock, MapPin, Users, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService, CourseAvailability, GolfCourse, TeeTimesParams } from '../services/api';
import { getLocalYYYYMMDD } from '../utils/localDateUtils';
import DatePicker from '../components/DatePicker';

function TeeTimesPage() {
  const [courses, setCourses] = useState<CourseAvailability[]>([]);
  const [golfCourses, setGolfCourses] = useState<GolfCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Filter states
  const [selectedDate, setSelectedDate] = useState<string>(getLocalYYYYMMDD());
  const [selectedCourses, setSelectedCourses] = useState<number[]>([]);
  const [startHour, setStartHour] = useState<number>(6);
  const [endHour, setEndHour] = useState<number>(20); // 扩展到晚上8点以包含更多时段
  const [numberOfPlayers, setNumberOfPlayers] = useState<number>(0);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(true); // collapsible filters panel

  // Load golf courses on mount
  useEffect(() => {
    loadGolfCourses();
  }, []);


  // Load tee times when filters change
  useEffect(() => {
    if (golfCourses.length > 0) {
      loadTeeTimesToday();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedCourses, startHour, endHour, numberOfPlayers, golfCourses]);

  const loadGolfCourses = async () => {
    try {
      const coursesData = await apiService.getGolfCourses();
      setGolfCourses(coursesData);
      // Don't auto-select any courses on first load
    } catch (error) {
      console.error('Failed to load golf courses:', error);
    }
  };

  const loadTeeTimesToday = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: TeeTimesParams = {
        date: selectedDate,
        courseIds: selectedCourses.length > 0 ? selectedCourses : undefined,
        startHour,
        endHour,
        numberOfPlayers
      };
      
      const data = await apiService.fetchTeeTimesByDate(params);
      setCourses(data);
      setLastUpdated(new Date());
    } catch (error: any) {
      setError(error.message || 'Failed to load tee times');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadTeeTimesToday();
  };

  // Note: handleCourseToggle reserved for future course filtering UI
  // const handleCourseToggle = (courseId: number) => {
  //   setSelectedCourses(prev => 
  //     prev.includes(courseId) 
  //       ? prev.filter(id => id !== courseId)
  //       : [...prev, courseId]
  //   );
  // };

  const formatTime = (timeStr: string): string => {
    if (!timeStr || !timeStr.includes(':')) {
      return timeStr;
    }
    
    const [hour, minute] = timeStr.split(':');
    const hourNum = parseInt(hour, 10);
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum > 12 ? hourNum - 12 : (hourNum === 0 ? 12 : hourNum);
    
    return `${displayHour}:${minute} ${period}`;
  };

  // 统一的卡片样式，不根据空位数量变化颜色
  const getCardStyle = (): string => {
    return 'bg-white text-gray-800 border-gray-200';
  };

  const handleSlotClick = (courseId: number) => {
    const course = golfCourses.find(c => c.id === courseId);
    const bookingUrl = course?.booking_url || 'https://golfvancouver.cps.golf/onlineresweb/search-teetime';
    window.open(bookingUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Layout
      title="Find Golf Tee Times in Metro Vancouver | Snag Your Tee"
      description="Search and book golf tee times across Metro Vancouver. Snag Your Tee covers 9 courses in Vancouver, Burnaby, and Richmond including Langara, Fraserview, McCleery, and more."
    >
      <main className="max-w-6xl mx-auto px-6 md:px-6 py-6 md:py-10 min-w-0">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Find and Book Golf Tee Times in Metro Vancouver
              </h1>
              {lastUpdated && (
                <p className="text-gray-600 text-sm">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="hidden md:flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors mt-4 md:mt-0 touch-manipulation min-h-[44px] font-medium"
            >
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
              <span>{loading ? 'Updating...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 border border-green-100 mb-6">
          {/* Collapse toggle (both desktop & mobile) */}
          <button
            type="button"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`w-full flex items-center justify-between py-2 font-medium text-gray-800 ${filtersOpen ? 'mb-4' : ''}`}
            aria-expanded={filtersOpen}
          >
            <span className="flex items-center"><Calendar className="mr-2" size={16} />Filters</span>
            {filtersOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          <div className={filtersOpen ? 'space-y-4 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 gap-4' : 'hidden'}>
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline mr-1" size={16} />
                Date
              </label>
              <DatePicker
                selectedDate={selectedDate}
                onChange={setSelectedDate}
                minDate={getLocalYYYYMMDD()}
                maxDate={(() => {
                  const today = new Date();
                  const maxDate = new Date(today);
                  maxDate.setDate(today.getDate() + 30);
                  return getLocalYYYYMMDD(maxDate);
                })()}
              />
            </div>

            {/* Time Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="inline mr-1" size={16} />
                Start Time
              </label>
              <div className="relative">
                <select
                  value={startHour}
                  onChange={(e) => {
                    const newStartHour = parseInt(e.target.value);
                    setStartHour(newStartHour);
                    // Auto-adjust end hour if it's not after start hour
                    if (endHour <= newStartHour) {
                      setEndHour(Math.min(newStartHour + 1, 22));
                    }
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 md:px-4 py-3 pr-10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-green-500 bg-white min-h-[48px] appearance-none cursor-pointer"
              >
                {Array.from({ length: 15 }, (_, i) => i + 6).map(hour => (
                  <option key={hour} value={hour}>
                    {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                  </option>
                ))}
                </select>
                {/* 自定义箭头图标 */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
              <div className="relative">
                <select
                  value={endHour}
                  onChange={(e) => setEndHour(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 md:px-4 py-3 pr-10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-green-500 bg-white min-h-[48px] appearance-none cursor-pointer"
              >
                {Array.from({ length: 15 }, (_, i) => i + 6)
                  .filter(hour => hour > startHour) // Only show hours after start hour
                  .map(hour => (
                    <option key={hour} value={hour}>
                      {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                    </option>
                  ))}
                </select>
                {/* 自定义箭头图标 */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Party Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline mr-1" size={16} />
                Party Size
              </label>
              <div className="relative">
                <select
                  value={numberOfPlayers}
                  onChange={(e) => setNumberOfPlayers(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 md:px-4 py-3 pr-10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-green-500 bg-white min-h-[48px] appearance-none cursor-pointer"
              >
                <option value={0}>Any</option>
                <option value={2}>2 Players</option>
                <option value={3}>3 Players</option>
                <option value={4}>4 Players</option>
                </select>
                {/* 自定义箭头图标 */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Courses - 2-Level Menu: Region -> Course */}
            <div className="md:col-span-2 lg:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <MapPin className="inline mr-1" size={16} />
                Courses
              </label>
              <div className="space-y-3">
                {/* Course regions */}
                {(() => {
                  // Group courses by region - use full location from database
                  const coursesByRegion = golfCourses.reduce((acc, course) => {
                    const region = course.location || 'Other';
                    
                    if (!acc[region]) acc[region] = [];
                    acc[region].push(course);
                    return acc;
                  }, {} as Record<string, typeof golfCourses>);
                  
                  return Object.entries(coursesByRegion).map(([region, courses]) => {
                    const regionCourseIds = courses.map(c => c.id);
                    const allRegionSelected = regionCourseIds.every(id => selectedCourses.includes(id));
                    
                    return (
                      <div key={region} className="bg-gray-50 rounded-lg p-3">
                        {/* Region and courses in aligned row */}
                        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                          {/* Clickable region title */}
                          <div className="w-full md:w-36 md:shrink-0">
                            <button
                              onClick={() => {
                                if (allRegionSelected) {
                                  // Deselect all courses in this region
                                  setSelectedCourses(prev => prev.filter(id => !regionCourseIds.includes(id)));
                                } else {
                                  // Select all courses in this region
                                  setSelectedCourses(prev => [
                                    ...prev.filter(id => !regionCourseIds.includes(id)),
                                    ...regionCourseIds
                                  ]);
                                }
                              }}
                              className={`px-2 py-2 rounded transition-all cursor-pointer text-left w-full touch-manipulation ${
                                allRegionSelected
                                  ? 'text-white bg-green-600'
                                  : 'text-gray-700 hover:text-green-600'
                              }`}
                            >
                              <h4 className="font-medium text-sm truncate">{region}</h4>
                            </button>
                          </div>
                          
                          {/* Mobile: Full width separator */}
                          <div className="h-px bg-gray-300 md:hidden"></div>
                          {/* Desktop: Vertical separator */}
                          <div className="hidden md:block w-px h-6 bg-gray-300 md:shrink-0"></div>
                          
                          {/* Region courses */}
                          <div className="flex flex-wrap gap-2 md:gap-3 flex-1">
                            {courses.map(course => (
                              <button
                                key={course.id}
                                onClick={() => {
                                  setSelectedCourses(prev =>
                                    prev.includes(course.id)
                                      ? prev.filter(id => id !== course.id)
                                      : [...prev, course.id]
                                  );
                                }}
                                className={`px-3 py-2 text-sm rounded transition-all cursor-pointer touch-manipulation min-h-[40px] ${
                                  selectedCourses.includes(course.id)
                                    ? 'text-white bg-green-600'
                                    : 'text-gray-700 hover:text-green-600'
                                }`}
                              >
                                <span>{course.name.replace(' Golf Course', '')}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile-only Refresh (under filters) */}
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="md:hidden w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors mb-6 touch-manipulation min-h-[44px] font-medium"
        >
          <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
          <span>{loading ? 'Updating...' : 'Refresh'}</span>
        </button>

        {/* Content */}
        <div className="space-y-6">
          {loading && (
            <div className="text-center py-8 md:py-12 px-4 md:px-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading tee times...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle className="text-red-600 mr-3" size={20} />
              <div>
                <p className="text-red-800 font-medium">Error loading tee times</p>
                <p className="text-red-600">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && (() => {
            // Check if there are any available slots across all courses
            const hasAvailableSlots = courses.some(course => 
              course.teeTimeSlots.some(slot => slot.availableSpots > 0)
            );
            return !hasAvailableSlots;
          })() && (
            <div className="text-center py-8 md:py-12 px-4 md:px-6 bg-white rounded-xl shadow-lg border border-green-100">
              <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Tee Times Available</h3>
              <p className="text-gray-600">No available times found for your selected criteria.</p>
            </div>
          )}

          {/* Course Results - Show only selected courses with available slots */}
          {!loading && !error && golfCourses
            .filter(golfCourse => selectedCourses.length === 0 || selectedCourses.includes(golfCourse.id))
            .map((golfCourse) => {
              // Find corresponding course data
              const courseData = courses.find(c => c.courseId === golfCourse.id);
              let availableSlots = courseData?.teeTimeSlots?.filter(slot => slot.availableSpots > 0) || [];
              
              // Filter out past times if selected date is today
              const today = getLocalYYYYMMDD();
              if (selectedDate === today) {
                const now = new Date();
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
                const currentTimeInMinutes = currentHour * 60 + currentMinute;
                
                availableSlots = availableSlots.filter(slot => {
                  const [slotHour, slotMinute] = slot.time.split(':').map(Number);
                  const slotTimeInMinutes = slotHour * 60 + slotMinute;
                  
                  // Keep slots that are at least 30 minutes in the future
                  return slotTimeInMinutes >= currentTimeInMinutes + 30;
                });
              }

              // Filter by time range (startHour - endHour)
              availableSlots = availableSlots.filter(slot => {
                const [hour, minute] = slot.time.split(':').map(Number);
                const slotTotalMinutes = hour * 60 + minute;
                const startTotalMinutes = startHour * 60;
                const endTotalMinutes = endHour * 60;
                
                // 时间匹配：在开始时间之后且在结束时间之前（不包含结束时间的整点）
                return slotTotalMinutes >= startTotalMinutes && slotTotalMinutes < endTotalMinutes;
              });
              
              // Only render courses with available slots
              if (availableSlots.length === 0) {
                return null;
              }
              
              return (
                <div key={golfCourse.id} className="bg-white rounded-xl shadow-lg p-4 md:p-6 border border-green-100">
                  {/* Course Header - Mobile optimized */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 pb-3 border-b border-gray-100">
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2 sm:mb-0">
                      {golfCourse.name}
                    </h3>
                    <span className="text-sm text-gray-600 bg-green-50 px-3 py-1 rounded-full self-start sm:self-auto">
                      {availableSlots.length} slot{availableSlots.length !== 1 ? 's' : ''} available
                    </span>
                  </div>

                  {/* Mobile: Single column, Desktop: Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {availableSlots.map((slot, index) => (
                      <div
                        key={index}
                        onClick={() => handleSlotClick(golfCourse.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 touch-manipulation min-h-[80px] ${getCardStyle()}`}
                      >
                        {/* Mobile-optimized layout */}
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-bold text-lg">{formatTime(slot.time)}</span>
                          {slot.price > 0 && (
                            <span className="text-lg font-semibold text-green-600">${slot.price}</span>
                          )}
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-gray-600">
                            {slot.availableSpots === 1
                              ? '1 spot available'
                              : `${slot.availableSpots} spots available`
                            }
                          </div>
                          <div className="text-xs text-green-600 font-medium flex items-center">
                            Book Now
                            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
            .filter(Boolean)}
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                How do I search for golf tee times in Metro Vancouver?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Use the Tee Times page to view availability across multiple public golf courses in Metro Vancouver, including Langara, Fraserview, McCleery, Burnaby, and Richmond. You can filter by date and time, then click through to book directly with the course.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                Can I see all courses at once?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Yes. Snag Your Tee shows available tee times from multiple courses in one place, so you don't need to open every course website separately.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                Do you provide real-time updates?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Yes. Tee times are refreshed in real time, meaning the availability you see matches what the golf course is currently offering.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                What if the course I want is sold out?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                If a course is fully booked, you can join the waitlist directly from the Tee Times page to be alerted if a spot opens up.
              </p>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default TeeTimesPage;